import type { z } from "zod";

import {
  slidesVisibleValuesDesktop,
  slidesVisibleValuesMobile,
} from "./carousel.constants";
import type {
  BadBuilderValue,
  ReportBadBuilderValue,
  StoredProductCarouselOptions,
  StoredRecsOptions,
} from "./types";

// Editor defaults for new blocks, and the fallback when a stored value is
// missing, picked from the dropdown lists the editor offers.
export const LAYOUT_DEFAULTS = {
  slidesVisible: slidesVisibleValuesDesktop[3],
  slidesVisibleMobile: slidesVisibleValuesMobile[1],
};

export const DEFAULT_NUM_RESULTS = 8;
// Same bound as productRecommendations; here a sanity limit on tiles and
// skeletons, since getProducts batches ids for the category path.
export const MAX_NUM_RESULTS = 24;
// The recs products load in one SCAPI getProducts call, which takes at most 24
// ids (SCAPI_GET_PRODUCTS_MAX_IDS in shop/shop.server.ts, a server-only module).
export const MAX_RECS_RESULTS = 24;
// The previous storefront's peek for a missing nested value (hec-composable
// origin/develop builder/blocks/product-carousel/productCarousel.jsx:31-39).
// Its missing slide counts were fill values with peek built in ("3.25" /
// "2.25"); here they take the editor defaults, so peek adds ".25" only once.
export const PREVIOUS_STOREFRONT_PEEK = {
  onDesktop: false,
  onTablet: false,
  onMobile: true,
};

// A union reports every branch; the deepest path is where the value came
// closest to fitting, so only those messages are kept.
export const describeProblem = (error: z.ZodError) => {
  const leaves = (issues: z.ZodIssue[]): z.ZodIssue[] =>
    issues.flatMap(issue =>
      issue.code === "invalid_union"
        ? issue.unionErrors.flatMap(union => leaves(union.issues))
        : [issue],
    );
  const all = leaves(error.issues);
  const depth = Math.max(...all.map(issue => issue.path.length));
  const messages = all
    .filter(issue => issue.path.length === depth)
    .map(issue =>
      depth ? `at ${issue.path.join(".")}: ${issue.message}` : issue.message,
    );
  return [...new Set(messages)].join(" or ");
};

// A reporter that collects each bad value as its field and problem. The value
// itself is not kept: a category option carries the category's SEO HTML, and
// Zod already names what failed.
export const collectBadBuilderValues = (): {
  report: ReportBadBuilderValue;
  take: () => BadBuilderValue[];
} => {
  const found: BadBuilderValue[] = [];
  return {
    report: (field, error) => {
      found.push({ field, problem: describeProblem(error) });
    },
    take: () => found.splice(0),
  };
};

// Flat keys win over the nested shape. A block without a flat categoryId was
// authored on the previous storefront, so its count comes from resultCount too.
export const mergeProductCarouselOptions = ({
  slidesVisible: slides,
  showPeek: peek,
  slidePadding: pad,
  ...flat
}: StoredProductCarouselOptions) => ({
  categoryId:
    flat.categoryId || flat.productCategory?.options.category || undefined,
  resultCount:
    flat.numResults ??
    (flat.categoryId ? undefined : flat.resultCount) ??
    DEFAULT_NUM_RESULTS,
  // The editor defaults also cover Builder's "Reset field" (undefined) and
  // missing nested counts, which would otherwise let the carousel's
  // peek-included fallbacks ("3.25" / "2.25") in.
  slidesVisible: slides?.onDesktop ?? LAYOUT_DEFAULTS.slidesVisible,
  slidesVisibleTablet: flat.slidesVisibleTablet ?? slides?.onTablet,
  slidesVisibleMobile:
    flat.slidesVisibleMobile ??
    slides?.onMobile ??
    LAYOUT_DEFAULTS.slidesVisibleMobile,
  showPeek: peek?.onDesktop,
  showPeekTablet: flat.showPeekTablet ?? peek?.onTablet,
  showPeekMobile: flat.showPeekMobile ?? peek?.onMobile,
  slidePadding: pad?.onDesktop,
  slidePaddingTablet: flat.slidePaddingTablet ?? pad?.onTablet,
  slidePaddingMobile: flat.slidePaddingMobile ?? pad?.onMobile,
  showNavigationArrowsTablet: flat.showNavigationArrowsTablet,
});

// The older recs block stores slides as { onDesktop, onMobile }, the newer one
// flat; a flat tablet or mobile count wins.
export const mergeRecsOptions = ({
  slidesVisible: slides,
  ...options
}: StoredRecsOptions) => ({
  ...options,
  slidesVisible: slides?.onDesktop,
  slidesVisibleTablet: options.slidesVisibleTablet ?? slides?.onTablet,
  slidesVisibleMobile: options.slidesVisibleMobile ?? slides?.onMobile,
});
